import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

/**
 * Principal -> processor onboarding. Separate concern from principal
 * creation (PrincipalSlice) and from program creation (ProgramSlice's
 * processor list) - a principal can be onboarded against any number of
 * processors, at any time, independently of both.
 */

/**
 * Processor-specific identifiers (YesBank's clientIdentifier/
 * ecollectAccountNumber, ICICI CBDC's sponsorId, ICICI's manually-recorded
 * mid/vpa) are never stored as dedicated DB columns - each processor returns
 * a different set of fields. The backend derives this object from
 * requestPayload/responsePayload on read; only the fields relevant to the
 * given processor's brand will be present.
 */
export interface PrincipalProcessorIdentifiers {
  clientIdentifier?: string | null;
  ecollectAccountNumber?: string | null;
  sponsorId?: string | null;
  mid?: string | null;
  vpa?: string | null;
  mypPrincipalId?: string | null;
}

export interface PrincipalProcessor {
  principalProcessorId: number;
  principalId: number;
  processorId: number;
  status: "pending" | "onboarded" | "failed";
  requestPayload: Record<string, any> | null;
  responsePayload: Record<string, any> | null;
  identifiers?: PrincipalProcessorIdentifiers;
  errorMessage: string | null;
  onboardedAt: string | null;
  createdAt: string;
  updatedAt: string;
  processor?: {
    processorId: number;
    name: string;
    processorType: string;
  };
}

export const fetchPrincipalProcessors = createAsyncThunk(
  "processorOnboarding/fetchPrincipalProcessors",
  async (principalId: string, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/principals/${principalId}/processors`,
        timeout: 15000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "GET",
      });

      return response.data?.principalProcessors as PrincipalProcessor[];
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch processor onboarding status"
      );
    }
  }
);

export const onboardPrincipalWithProcessor = createAsyncThunk(
  "processorOnboarding/onboardPrincipalWithProcessor",
  async (
    { principalId, processorId, payload }: { principalId: string; processorId: number; payload: Record<string, any> },
    { rejectWithValue }
  ) => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/principals/${principalId}/processors/${processorId}/onboard`,
        timeout: 30000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "POST",
        data: payload,
      });

      return response.data?.principalProcessor as PrincipalProcessor;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to onboard principal with processor"
      );
    }
  }
);

interface ProcessorOnboardingState {
  principalProcessors: PrincipalProcessor[] | null;
  principalProcessorsLoading: boolean;
  principalProcessorsError: string | null;
  onboardLoading: boolean;
  onboardError: string | null;
  lastOnboardedPrincipalProcessor: PrincipalProcessor | null;
}

const initialState: ProcessorOnboardingState = {
  principalProcessors: null,
  principalProcessorsLoading: false,
  principalProcessorsError: null,
  onboardLoading: false,
  onboardError: null,
  lastOnboardedPrincipalProcessor: null,
};

const processorOnboardingSlice = createSlice({
  name: "processorOnboarding",
  initialState,
  reducers: {
    clearOnboardError(state) {
      state.onboardError = null;
    },
    clearLastOnboardedPrincipalProcessor(state) {
      state.lastOnboardedPrincipalProcessor = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPrincipalProcessors.pending, (state) => {
        state.principalProcessorsLoading = true;
        state.principalProcessorsError = null;
      })
      .addCase(fetchPrincipalProcessors.fulfilled, (state, action: PayloadAction<PrincipalProcessor[]>) => {
        state.principalProcessorsLoading = false;
        state.principalProcessors = action.payload;
      })
      .addCase(fetchPrincipalProcessors.rejected, (state, action) => {
        state.principalProcessorsLoading = false;
        state.principalProcessorsError = action.payload as string;
      })

      .addCase(onboardPrincipalWithProcessor.pending, (state) => {
        state.onboardLoading = true;
        state.onboardError = null;
      })
      .addCase(onboardPrincipalWithProcessor.fulfilled, (state, action: PayloadAction<PrincipalProcessor>) => {
        state.onboardLoading = false;
        state.lastOnboardedPrincipalProcessor = action.payload;
        if (state.principalProcessors) {
          const idx = state.principalProcessors.findIndex((p) => p.processorId === action.payload.processorId);
          if (idx >= 0) {
            state.principalProcessors[idx] = action.payload;
          } else {
            state.principalProcessors.push(action.payload);
          }
        } else {
          state.principalProcessors = [action.payload];
        }
      })
      .addCase(onboardPrincipalWithProcessor.rejected, (state, action) => {
        state.onboardLoading = false;
        state.onboardError = action.payload as string;
      });
  },
});

export const { clearOnboardError, clearLastOnboardedPrincipalProcessor } = processorOnboardingSlice.actions;
export default processorOnboardingSlice.reducer;
